import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Save, Upload, X, GripVertical, Star } from 'lucide-react';
import { toast } from 'sonner';
import { LocationInput } from '@/components/LocationInput';
import { WorkshopEditor } from './WorkshopEditor';
import { getErrorMessage } from '@/lib/errorMessage';
import { IMAGE_ACCEPT, skippedPhotosToast, storageUploadMessage, validateImageFile, withImageType } from '@/lib/imageUpload';

interface DateRange {
  id?: string;
  start_date: string;
  end_date: string;
  notes: string;
}

interface PhotoEntry {
  id?: string;
  photo_url: string;
  alt_text: string;
  sort_order: number;
  file?: File;
}

interface ListingFormData {
  title: string;
  location_name: string;
  latitude: number;
  longitude: number;
  price_display: string;
  price_cents: number;
  max_participants: number | null;
  workshop_type: string;
  description_html: string;
  external_url: string;
  dates: DateRange[];
  photos: PhotoEntry[];
}

interface ExistingListing {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  latitude: number;
  longitude: number;
  price_display: string;
  price_cents?: number;
  max_participants: number | null;
  workshop_type: string;
  description_html: string;
  external_url: string;
  is_active?: boolean;
  created_at?: string;
  workshop_dates: Array<{ id?: string; start_date: string; end_date: string; notes: string | null; sort_order?: number }>;
  workshop_photos: Array<{ id?: string; photo_url: string; alt_text: string | null; sort_order: number }>;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leaderId: string;
  existingListing?: ExistingListing | null;
  onSave: () => void;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 80) + '-' + Math.random().toString(36).substring(2, 7);
}

export const WorkshopListingEditor: React.FC<Props> = ({ open, onOpenChange, leaderId, existingListing, onSave }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const buildFormFromListing = (listing: ExistingListing): ListingFormData => ({
    title: listing.title,
    location_name: listing.location_name,
    latitude: listing.latitude,
    longitude: listing.longitude,
    price_display: listing.price_display || '',
    price_cents: listing.price_cents || 0,
    max_participants: listing.max_participants,
    workshop_type: listing.workshop_type,
    description_html: listing.description_html || '',
    external_url: listing.external_url || '',
    dates: listing.workshop_dates?.length > 0 ? (listing.workshop_dates as DateRange[]) : [{ start_date: '', end_date: '', notes: '' }],
    photos: (listing.workshop_photos || []) as PhotoEntry[],
  });

  const emptyForm: ListingFormData = {
    title: '',
    location_name: '',
    latitude: 0,
    longitude: 0,
    price_display: '',
    price_cents: 0,
    max_participants: null,
    workshop_type: 'astrophotography',
    description_html: '',
    external_url: '',
    dates: [{ start_date: '', end_date: '', notes: '' }],
    photos: [],
  };

  const [form, setForm] = useState<ListingFormData>(
    existingListing ? buildFormFromListing(existingListing) : emptyForm
  );

  // Reset form when dialog opens with a different listing
  React.useEffect(() => {
    if (open) {
      setForm(existingListing ? buildFormFromListing(existingListing) : emptyForm);
    }
  }, [open, existingListing]);

  const handleLocationSelect = (location: { name: string; latitude: number; longitude: number } | null) => {
    if (location) {
      setForm({ ...form, location_name: location.name, latitude: location.latitude, longitude: location.longitude });
    }
  };

  const addDateRange = () => {
    setForm({ ...form, dates: [...form.dates, { start_date: '', end_date: '', notes: '' }] });
  };

  const removeDateRange = (index: number) => {
    setForm({ ...form, dates: form.dates.filter((_, i) => i !== index) });
  };

  const updateDateRange = (index: number, field: string, value: string) => {
    const updated = [...form.dates];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, dates: updated });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const picked = Array.from(input.files ?? []);
    // Reset now (the files are copied) so the same file can be picked again.
    input.value = '';
    if (picked.length === 0 || !user) return;

    const currentCount = form.photos.length;
    const remaining = 5 - currentCount;
    if (remaining <= 0) {
      toast.error('Maximum 5 photos per workshop');
      return;
    }

    // Refuse bad files before any upload, and don't let them use up photo
    // slots. The good ones still upload (ERI-2836).
    const skipped: string[] = [];
    const filesToUpload = picked.filter((file) => {
      const problem = validateImageFile(file);
      if (problem) skipped.push(problem);
      return !problem;
    }).slice(0, remaining);

    for (const file of filesToUpload) {
      try {
        const ext = file.name.split('.').pop();
        const path = `${user.id}/workshops/${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('workshop-images')
          .upload(path, withImageType(file));

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('workshop-images')
          .getPublicUrl(path);

        setForm(prev => ({
          ...prev,
          photos: [...prev.photos, {
            photo_url: urlData.publicUrl,
            alt_text: '',
            sort_order: prev.photos.length + 1,
          }],
        }));
      } catch (err) {
        skipped.push(storageUploadMessage(err));
      }
    }

    if (skipped.length > 0) {
      const { title, description } = skippedPhotosToast(skipped);
      toast.error(title, { description });
    }
  };

  const removePhoto = (index: number) => {
    setForm({
      ...form,
      photos: form.photos.filter((_, i) => i !== index).map((p, i) => ({ ...p, sort_order: i + 1 })),
    });
  };

  const setFeaturedPhoto = (index: number) => {
    const photos = [...form.photos];
    const featured = photos.splice(index, 1)[0];
    photos.unshift(featured);
    setForm({
      ...form,
      photos: photos.map((p, i) => ({ ...p, sort_order: i + 1 })),
    });
  };

  const handlePriceChange = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, '');
    const cents = Math.round(parseFloat(cleaned || '0') * 100);
    setForm({ ...form, price_display: value, price_cents: cents });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.location_name.trim()) { toast.error('Location is required'); return; }
    if (form.dates.length === 0 || !form.dates[0].start_date) { toast.error('At least one date range is required'); return; }

    setSaving(true);
    try {
      const slug = existingListing?.slug || generateSlug(form.title);

      const listingData = {
        slug,
        leader_id: leaderId,
        title: form.title,
        description_html: form.description_html,
        location_name: form.location_name,
        latitude: form.latitude,
        longitude: form.longitude,
        price_cents: form.price_cents,
        price_display: form.price_display,
        max_participants: form.max_participants,
        workshop_type: form.workshop_type,
        external_url: form.external_url,
        is_active: true,
      };

      let listingId: string;

      if (existingListing) {
        const { error } = await supabase
          .from('workshop_listings')
          .update(listingData)
          .eq('id', existingListing.id);
        if (error) throw error;
        listingId = existingListing.id;

        // Delete existing dates and photos to re-insert
        await supabase.from('workshop_dates').delete().eq('listing_id', listingId);
        await supabase.from('workshop_photos').delete().eq('listing_id', listingId);
      } else {
        const { data, error } = await supabase
          .from('workshop_listings')
          .insert(listingData)
          .select('id')
          .single();
        if (error) throw error;
        listingId = data.id;
      }

      // Insert dates
      const validDates = form.dates.filter(d => d.start_date && d.end_date);
      if (validDates.length > 0) {
        const { error: datesError } = await supabase
          .from('workshop_dates')
          .insert(validDates.map((d, i) => ({
            listing_id: listingId,
            start_date: d.start_date,
            end_date: d.end_date,
            sort_order: i,
            notes: d.notes || null,
          })));
        if (datesError) throw datesError;
      }

      // Insert photos
      if (form.photos.length > 0) {
        const { error: photosError } = await supabase
          .from('workshop_photos')
          .insert(form.photos.map((p, i) => ({
            listing_id: listingId,
            photo_url: p.photo_url,
            alt_text: p.alt_text || null,
            sort_order: i + 1,
          })));
        if (photosError) throw photosError;
      }

      // Notify admin of new listing (not on edits). invoke() sends the signed-in
      // user's token. The function reads the listing, its leader and dates from
      // the database by slug, so only the slug is sent (ERI-2609).
      if (!existingListing) {
        supabase.functions.invoke('notify-workshop-created', {
          body: { slug },
        }).catch((err) => console.error('Notification email failed:', err));
      }

      toast.success(existingListing ? 'Workshop updated' : 'Workshop created');
      onOpenChange(false);
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(getErrorMessage(err) || 'Failed to save workshop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existingListing ? 'Edit Workshop' : 'Add Workshop'}</DialogTitle>
          <DialogDescription>
            {existingListing ? 'Update your workshop listing details.' : 'Add a workshop to your directory listing.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Title */}
          <div>
            <Label htmlFor="title">Workshop Title *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Milky Way Photography Masterclass"
            />
          </div>

          {/* Location */}
          <div>
            <Label>Location *</Label>
            <LocationInput
              onLocationSelect={handleLocationSelect}
              initialLocation={form.location_name ? { name: form.location_name, latitude: form.latitude, longitude: form.longitude } : undefined}
            />
            {form.location_name && (
              <p className="text-xs text-muted-foreground mt-1">
                {form.location_name} ({form.latitude.toFixed(4)}°, {form.longitude.toFixed(4)}°)
              </p>
            )}
          </div>

          {/* Date Ranges */}
          <div>
            <Label>Dates *</Label>
            <div className="space-y-3 mt-2">
              {form.dates.map((dateRange, i) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Start</Label>
                    <Input
                      type="date"
                      value={dateRange.start_date}
                      onChange={(e) => updateDateRange(i, 'start_date', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">End</Label>
                    <Input
                      type="date"
                      value={dateRange.end_date}
                      onChange={(e) => updateDateRange(i, 'end_date', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      value={dateRange.notes}
                      onChange={(e) => updateDateRange(i, 'notes', e.target.value)}
                      placeholder="e.g. Week 1"
                    />
                  </div>
                  {form.dates.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeDateRange(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addDateRange} className="gap-1">
                <Plus className="w-3 h-3" /> Add Date Range
              </Button>
            </div>
          </div>

          {/* Price + Participants + Type */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                value={form.price_display}
                onChange={(e) => handlePriceChange(e.target.value)}
                placeholder="$2,500"
              />
            </div>
            <div>
              <Label htmlFor="max_participants">Max Participants</Label>
              <Input
                id="max_participants"
                type="number"
                value={form.max_participants || ''}
                onChange={(e) => setForm({ ...form, max_participants: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="12"
              />
            </div>
            <div>
              <Label>Workshop Type</Label>
              <Select value={form.workshop_type} onValueChange={(v) => setForm({ ...form, workshop_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="astrophotography">Astrophotography</SelectItem>
                  <SelectItem value="deep_sky">Deep Sky</SelectItem>
                  <SelectItem value="landscape">Landscape</SelectItem>
                  <SelectItem value="nature">Nature</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <Label>Description</Label>
            <WorkshopEditor
              content={form.description_html}
              onChange={(html) => setForm({ ...form, description_html: html })}
              placeholder="Describe what photographers will learn, what's included, experience level, gear requirements..."
            />
          </div>

          {/* Photos */}
          <div>
            <Label>Photos (up to 5) — click the star to set featured image</Label>
            <div className="grid grid-cols-5 gap-3 mt-2">
              {form.photos.map((photo, i) => (
                <div key={i} className={`relative aspect-square rounded-[4px] overflow-hidden bg-muted group ${i === 0 ? 'ring-2 ring-[#c9922a]' : ''}`}>
                  <img src={photo.photo_url} alt={photo.alt_text || ''} className="w-full h-full object-cover" />
                  {/* Featured badge */}
                  {i === 0 && (
                    <div className="absolute top-1 left-1 bg-[#c9922a] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-[2px]">
                      Featured
                    </div>
                  )}
                  {/* Set as featured */}
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => setFeaturedPhoto(i)}
                      aria-label="Set photo as featured"
                      title="Set as featured image"
                      className="absolute top-1 left-1 bg-black/60 rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                      <Star className="w-3 h-3 text-white" />
                    </button>
                  )}
                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    aria-label="Remove photo"
                    className="absolute top-1 right-1 bg-black/60 rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
              {form.photos.length < 5 && (
                <label className="aspect-square rounded-md border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="file"
                    accept={IMAGE_ACCEPT}
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                    aria-label="Upload workshop photos"
                  />
                  <Upload className="w-6 h-6 text-muted-foreground" />
                </label>
              )}
            </div>
          </div>

          {/* External URL */}
          <div>
            <Label htmlFor="external_url">Booking / Website URL</Label>
            <Input
              id="external_url"
              value={form.external_url}
              onChange={(e) => setForm({ ...form, external_url: e.target.value })}
              placeholder="https://yoursite.com/workshop-registration"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : existingListing ? 'Update Workshop' : 'Create Workshop'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
